import type { ChatPlayerMessage, ChatPlayerPartTiming, ChatPlayerTimelineEvent } from "./chat-player-types";
import { simpleStringHash } from "@mw/shared/hash";
import { clampMs, makeStableId } from "./chat-player-timing";

const EVENT_PRIORITY: Record<ChatPlayerTimelineEvent["type"], number> = {
  message: 0,
  part: 1,
  word: 2,
  tool: 3,
};

export function normalizeChatPlayerCompiledChat({
  id,
  messages,
  partTimings,
  durationMs,
}: {
  id: string;
  messages: ChatPlayerMessage[];
  partTimings: ChatPlayerPartTiming[];
  durationMs?: number;
}) {
  const messageIds = new Set<string>();
  const partIds = new Set<string>();
  const partTimingById = new Map<string, ChatPlayerPartTiming>();

  for (const timing of partTimings) {
    const key = timingKey(timing.messageId, timing.partId);

    if (partTimingById.has(key)) {
      throw new Error(`Duplicate part timing for ${timing.messageId}/${timing.partId}`);
    }

    partTimingById.set(key, {
      ...timing,
      startMs: clampMs(timing.startMs),
      endMs: timing.endMs === undefined ? undefined : clampMs(timing.endMs),
    });
  }

  const timeline: ChatPlayerTimelineEvent[] = [];
  let derivedDurationMs = 0;

  for (const message of messages) {
    if (message.role !== "user" && message.role !== "assistant") {
      throw new Error(`Unsupported chat role for message ${message.id}`);
    }

    if (messageIds.has(message.id)) {
      throw new Error(`Duplicate message id: ${message.id}`);
    }

    messageIds.add(message.id);

    let messageStartMs = Number.POSITIVE_INFINITY;
    let messageEndMs = 0;

    for (const part of message.parts) {
      if (partIds.has(part.id)) {
        throw new Error(`Duplicate part id: ${part.id}`);
      }

      partIds.add(part.id);

      const timing = partTimingById.get(timingKey(message.id, part.id));

      if (!timing) {
        throw new Error(`Missing part timing for ${message.id}/${part.id}`);
      }

      const partEndMs = resolvePartEndMs(part, timing);
      messageStartMs = Math.min(messageStartMs, timing.startMs);
      messageEndMs = Math.max(messageEndMs, partEndMs);
      derivedDurationMs = Math.max(derivedDurationMs, partEndMs);

      timeline.push({
        id: makeStableId(["part", message.id, part.id]),
        type: "part",
        messageId: message.id,
        partId: part.id,
        startMs: timing.startMs,
        endMs: partEndMs,
      });

      if (part.type === "speech") {
        for (const word of part.words) {
          const wordStartMs = clampMs(word.startMs);
          const wordEndMs = Math.max(wordStartMs, clampMs(word.endMs));

          timeline.push({
            id: makeStableId(["word", message.id, part.id, word.id]),
            type: "word",
            messageId: message.id,
            partId: part.id,
            wordId: word.id,
            startMs: wordStartMs,
            endMs: wordEndMs,
          });

          derivedDurationMs = Math.max(derivedDurationMs, wordEndMs);
          messageEndMs = Math.max(messageEndMs, wordEndMs);
        }
      }

      if (part.type === "tool") {
        timeline.push({
          id: makeStableId(["tool", message.id, part.id]),
          type: "tool",
          messageId: message.id,
          partId: part.id,
          startMs: timing.startMs,
          endMs: partEndMs,
        });
      }
    }

    if (message.parts.length > 0) {
      const startMs = Number.isFinite(messageStartMs) ? messageStartMs : 0;

      timeline.push({
        id: makeStableId(["message", message.id]),
        type: "message",
        messageId: message.id,
        startMs,
        endMs: messageEndMs,
      });
    }
  }

  timeline.sort(sortTimelineEvents);

  const normalizedDurationMs = Math.max(derivedDurationMs, clampMs(durationMs ?? 0));

  return {
    id,
    scopeKey: createScopeKey(messages, timeline, normalizedDurationMs),
    messages,
    timeline,
    durationMs: normalizedDurationMs,
  };
}

function createScopeKey(messages: ChatPlayerMessage[], timeline: ChatPlayerTimelineEvent[], durationMs: number) {
  return `chat-player-${(simpleStringHash(stableStringify({ durationMs, messages, timeline })) >>> 0).toString(36)}`;
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value) ?? "";
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }

  const record = value as Record<string, unknown>;

  return `{${Object.keys(record)
    .sort()
    .map(key => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
    .join(",")}}`;
}

function resolvePartEndMs(part: ChatPlayerMessage["parts"][number], timing: ChatPlayerPartTiming) {
  if (typeof timing.endMs === "number") {
    return Math.max(timing.startMs, timing.endMs);
  }

  if (part.type === "speech" && part.words.length > 0) {
    return Math.max(...part.words.map(word => word.endMs));
  }

  return timing.startMs;
}

function sortTimelineEvents(a: ChatPlayerTimelineEvent, b: ChatPlayerTimelineEvent) {
  if (a.startMs !== b.startMs) {
    return a.startMs - b.startMs;
  }

  const priorityDelta = EVENT_PRIORITY[a.type] - EVENT_PRIORITY[b.type];

  if (priorityDelta !== 0) {
    return priorityDelta;
  }

  return a.id.localeCompare(b.id);
}

function timingKey(messageId: string, partId: string) {
  return `${messageId}:${partId}`;
}
